"use strict";

// Admin view of every QA reviewer's status per language, with Return for
// submitted reviews so they reappear on that reviewer's dashboard.
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { getLanguageName } from "../../utils/generalFunctions";
import { getQAReviews, returnQAReview } from "./api";
import {
    QA_STATUS,
    QA_STATUS_COLORS,
    formatQADate,
    qaStatusLabel,
} from "./status";

const QAReviewPanel = ({ projectId }) => {
    const [reviews, setReviews] = useState(null);
    const [returning, setReturning] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await getQAReviews(projectId);
            setReviews(data.reviews || []);
        } catch (error) {
            console.error("Unable to load QA reviews", error);
            setReviews([]);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) load();
    }, [projectId, load]);

    const handleReturn = async () => {
        setBusy(true);
        try {
            await returnQAReview({
                projectId,
                language: returning.language,
                userId: returning.userId,
                note,
            });
            toast.success(`Returned to ${returning.name || "the reviewer"}`);
            setReturning(null);
            setNote("");
            await load();
        } catch (error) {
            toast.error(error?.response?.data?.error || "Unable to return the review");
        } finally {
            setBusy(false);
        }
    };

    if (!reviews?.length) return null;

    return (
        <>
            <Table size="small" sx={{ width: "100%", bgcolor: "white", mt: 4 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>QA Reviewer</TableCell>
                        <TableCell>Language</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Submitted</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {reviews.map((review) => (
                        <TableRow key={`${review.userId}-${review.language}`}>
                            <TableCell>{review.name || review.userId}</TableCell>
                            <TableCell>{getLanguageName(review.language)}</TableCell>
                            <TableCell>
                                <Chip
                                    size="small"
                                    label={qaStatusLabel(review.status)}
                                    color={QA_STATUS_COLORS[review.status]}
                                />
                            </TableCell>
                            <TableCell>{formatQADate(review.submittedAt)}</TableCell>
                            <TableCell align="right">
                                {review.status === QA_STATUS.SUBMITTED && (
                                    <Button size="small" onClick={() => setReturning(review)}>
                                        Return
                                    </Button>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog
                open={Boolean(returning)}
                onClose={() => !busy && setReturning(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Return the {returning && getLanguageName(returning.language)} review to{" "}
                    {returning?.name || "the reviewer"}?
                </DialogTitle>
                <DialogContent>
                    <TextField
                        label="Note for the reviewer (optional)"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        multiline
                        minRows={2}
                        fullWidth
                        sx={{ mt: 1 }}
                        inputProps={{ maxLength: 2000 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReturning(null)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleReturn} disabled={busy}>
                        Return
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default QAReviewPanel;
